/**
 * Project management operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class ProjectManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listProjects(
    page = 1,
    size = 20,
    name?: string,
    catalogUuid?: string,
  ): Promise<Record<string, any>> {
    const params: Record<string, any> = { current: page, size };
    if (name) params.name = name;
    if (catalogUuid) params.catalogUuid = catalogUuid;
    return this.session.get('/api/cdp/sys_project/extend/page', { params });
  }

  async getProject(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/sys_project/extend/get/${uuid}`);
  }

  async createProject(
    name: string,
    code: string,
    description?: string,
    catalogUuid?: string,
    generateDemoData = false,
  ): Promise<Record<string, any>> {
    const data = {
      context:{},
      data: {
        name,
        code,
        description,
        catalogUuid,
        generateDemoData: generateDemoData ? 1 : 0,
      },
    };
    return this.session.post('/api/cdp/sys_project/extend/createProject', { data });
  }

  async copyProject(
    srcUuid: string,
    name: string,
    code: string,
    tenantId?: number,
  ): Promise<Record<string, any>> {
    const data = {
      context: {},
      data: {
        srcProjectUuid: srcUuid,
        name,
        code,
        tenantId,
      },
    };
    return this.session.post('/api/cdp/sys_project/extend/copy', { data });
  }

  async createFromTemplate(
    templateUuid: string,
    name: string,
    code: string,
    generateDemoData = false,
  ): Promise<Record<string, any>> {
    const data = {
      context: {},
      data: {
        srcProjectUuid: templateUuid,
        name,
        code,
        generateDemoData: generateDemoData ? 1 : 0,
      },
    };
    return this.session.post('/api/cdp/sys_project/extend/createProject', { data });
  }

  /** Export project as Blob. */
  async exportProject(uuid: string): Promise<Blob> {
    const formData = new FormData();
    formData.append('uuid', uuid);
    return this.session.request('POST', 'api/cdp/sys_project/extend/export', {
      data: formData,
      responseType: 'blob',
    });
  }

  async importProject(file: File | Blob, importStrategy?: number): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    const params: Record<string, any> = {};
    if (importStrategy !== undefined) params.importStrategy = importStrategy;
    return this.session.post('/api/cdp/sys_project/extend/upload2', {
      data: formData,
      params,
      headers: {}, // Let browser set multipart boundary
    });
  }

  async deleteProject(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/sys_project/extend/delete/${uuid}`);
  }

  async getProjectDependencies(sceneUuid: string): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/sys_project/extend/dependencies', { data: { uuid: sceneUuid } });
  }

  async listTemplates(page = 1, size = 20): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project/extend/pageProjectTemplate', {
      params: { current: page, size },
    });
  }

  async getProjectUsers(projectUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project/extend/getProjectUserData', {
      params: { projectUuid },
    });
  }

  async assignUser(projectUuid: string, userIds: number[], roleType = 1): Promise<Record<string, any>> {
    const data = {
      context: {},
      data: {
        projectUuid,
        userIds,
        roleType,
      },
    };
    return this.session.post('/api/cdp/sys_project/extend/assignUser', { data });
  }

  async getProjectProfile(projectUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project/extend/getProjectProfile', {
      params: { projectUuid },
    });
  }

  async setAsTemplate(uuid: string, templated = true): Promise<Record<string, any>> {
    const data = { data: { uuid, templated: templated ? 1 : 0 } };
    return this.session.post('/api/cdp/sys_project/extend/template', { data });
  }

  // --- Extended project APIs ---

  async importToExisting(projectUuid: string, file: File | Blob, importStrategy?: number): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    const params: Record<string, any> = { projectUuid };
    if (importStrategy !== undefined) params.importStrategy = importStrategy;
    return this.session.post('/api/cdp/sys_project/extend/uploadExist', { data: formData, params, headers: {} });
  }

  async uploadWatermarkLogo(projectUuid: string, logoFile: File | Blob): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('projectUuid', projectUuid);
    formData.append('logoFile', logoFile);
    return this.session.post('/api/cdp/sys_project_config/uploadWatermarkLogo', {
      data: formData,
      headers: {},
    });
  }

  async listFunctions(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/project_function/list', { params });
  }

  async listRoleFunctions(roleUuid: string, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/project_role/listRoleFunction', { params: { roleUuid, ...params } });
  }

  async assignFunction(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/project_role/assignFunction', { data });
  }

  async pageRolePermissions(page = 1, size = 20, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/project_role/pageResourcePermission', {
      params: { current: page, size, ...params },
    });
  }

  async listRolePermissions(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/project_role/listResourcePermission', { params });
  }

  async tableAssignFunction(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/table/assignFunction', { data });
  }

  async getProjectUserRoleData(projectUuid: string, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/users/extend/getProjectUserRoleData', {
      params: { projectUuid, ...params },
    });
  }

  async assignUserRole(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/users/extend/assignRole', { data });
  }

  // --- Project base & statistics APIs ---

  async pageProjects(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project/page', { params });
  }

  async listProjectsInTenant(tenantId: number | string): Promise<Record<string, any>> {
    const params = {
      size: 1000,
      page: 1,
      options: JSON.stringify({ where: { templated: 0, tenantId } }),
    };
    return this.session.get('/api/cdp/sys_project/page', { params });
  }

  async getProjectByCode(code: string): Promise<Record<string, any>> {
    const params = {
      page: 1,
      size: 1,
      options: JSON.stringify({ where: { code } }),
    };
    return this.session.get('/api/cdp/sys_project/page', { params });
  }

  async listTemplateProjects(): Promise<Record<string, any>> {
    const params = { size: 1000, page: 1 };
    return this.session.get('/api/cdp/sys_project/extend/pageTemplateProject', { params });
  }

  async exportProjectAsync(uuid: string, data?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/sys_project/extend/asyncExport/${uuid}`, { data });
  }

  async exportProjectStaticAsync(uuid: string, data?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/sys_project/extend/asyncExportStatic/${uuid}`, { data });
  }

  async releaseProjectOnline(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/sys_project/extend/release_online', { data: { context: {}, data } });
  }

  async getProjectDevicetypeData(params: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project/extend/getProjectDevicetypeData', { params });
  }

  async getProjectResStatistics(projectUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project/extend/getProjectResStatistics', {
      params: { projectUuid },
    });
  }

  async getProjectAssetStatistics(projectUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project/extend/getProjectAssetStatistics', {
      params: { projectUuid },
    });
  }

  async getProjectStatisticsDetail(projectUuid: string, type?: string, postfix?: string): Promise<Record<string, any>> {
    const params: Record<string, any> = { projectUuid };
    if (type !== undefined) params.type = type;
    if (postfix !== undefined) params.postfix = postfix;
    return this.session.get('/api/cdp/sys_project/extend/getProjectStatisticsDetail', { params });
  }

  async getUsers(projectUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project/extend/getUsers', { params: { projectUuid } });
  }

  async getPublishURL(projectUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project/extend/getPublishURL', { params: { projectUuid } });
  }

  async getWatermarkConfig(projectUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_scene/extend/watermarkConfig', { params: { projectUuid } });
  }

  // --- Project config APIs ---

  async getProjectConfig(projectUuid: string, fields?: string[]): Promise<Record<string, any>> {
    const params: Record<string, any> = { projectUuid };
    if (fields) params.fields = fields;
    return this.session.get('/api/cdp/sys_project_config/list', { params });
  }

  async saveProjectConfig(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/sys_project_config/save', { data });
  }

  async getDataModelHierarchyAttrs(projectUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project_config/getDataModelHierarchyAttrs', {
      params: { projectUuid },
    });
  }

  // --- Project role APIs ---

  async pageProjectRoles(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/project_role/extend/page', { params });
  }

  async listProjectRoles(projectUuid: string): Promise<Record<string, any>> {
    const params = {
      size: 1000,
      page: 1,
      options: JSON.stringify({ where: { projectUuid } }),
    };
    return this.session.get('/api/cdp/project_role/page', { params });
  }

  async getProjectRole(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/project_role/get/${uuid}`);
  }

  async createProjectRole(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/project_role/create', { data });
  }

  async updateProjectRole(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/project_role/update', { data });
  }

  async deleteProjectRole(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/project_role/delete/${uuid}`);
  }

  async batchDeleteProjectRoles(uuids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/project_role/batchDelete', { data: uuids });
  }

  async listCatalogPermissions(params: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/project_role/listCatalogPermission', { params });
  }

  async getRoleUserData(projectUuid: string, roleUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/project_role/getRoleUserData', {
      params: { projectUuid, roleUuid },
    });
  }

  async assignRoleUser(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/project_role/assignUser', { data: { context: {}, data } });
  }

  // --- Table permission APIs ---

  async listMdpTables(projectUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/table/list', { params: { projectUuid } });
  }

  async getMdpTable(tableUuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/table/get/${tableUuid}`);
  }

  async pageTablePermissions(params: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/table/pageTablePermission', { params });
  }
}
